// Servicio de registro de empresas con Supabase
// @author Ana Lizcano y Alejandro Ángel
// @version 2.0.0
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const COMPANY_ENUMS = {
  estado_empresa: ['demo', 'prueba', 'activo', 'inactivo']
};

const AUTH_ERROR_MESSAGES = {
  'User already registered': 'Este email ya está registrado',
  'user_already_exists': 'Este email ya está registrado',
  'Invalid email': 'El formato del email no es válido',
  'Password should be at least 6 characters': 'La contraseña debe tener al menos 6 caracteres',
  'Signup is disabled': 'El registro está deshabilitado temporalmente',
  'Email rate limit exceeded': 'Se ha excedido el límite de intentos. Intenta más tarde',
  'Invalid password': 'La contraseña no cumple con los requisitos',
  'Weak password': 'La contraseña es muy débil',
  'Email not confirmed': 'El email no ha sido confirmado'
};

const registerCompanyService = {
  validateCompanyData(companyData) {
    const errors = [];

    if (!companyData.email || !companyData.email.includes('@')) {
      errors.push('El email debe ser válido');
    }

    if (!companyData.password || companyData.password.length < 6) {
      errors.push('La contraseña debe tener al menos 6 caracteres');
    }

    const requiredFields = [
      { field: 'nombre_empresa', name: 'Nombre de empresa' },
      { field: 'nit', name: 'NIT' },
      { field: 'telefono', name: 'Teléfono' },
      { field: 'pais_operacion', name: 'País de operación' },
      { field: 'ciudad_operacion', name: 'Ciudad de operación' },
      { field: 'tamaño_empresa', name: 'Tamaño de empresa' },
      { field: 'sector_empresa', name: 'Sector de empresa' }
    ];

    requiredFields.forEach(({ field, name }) => {
      if (!companyData[field] || companyData[field].toString().trim() === '') {
        errors.push(`${name} es requerido`);
      }
    });

    if (companyData.estado_empresa && !COMPANY_ENUMS.estado_empresa.includes(companyData.estado_empresa)) {
      errors.push('Estado de empresa no válido');
    }

    if (errors.length > 0) {
      throw { message: `Errores de validación: ${errors.join(', ')}` };
    }
  },

  handleAuthError(authData, statusCode) {
    if (statusCode === 422) {
      const errorKey = authData.error_code || authData.error_description || authData.message || authData.msg || '';
      const translatedMessage = AUTH_ERROR_MESSAGES[errorKey] || errorKey;
      return `Error de validación (422): ${translatedMessage}`;
    }

    const errorKey = authData.error_code || authData.error_description || authData.message || authData.msg || '';
    return AUTH_ERROR_MESSAGES[errorKey] || `Error de autenticación: ${errorKey}`;
  },

  async registerCompany(companyData) {
    try {
      if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
        throw { message: 'Error de configuración: Variables de entorno de Supabase no encontradas' };
      }

      this.validateCompanyData(companyData);

      
      const authPayload = {
        email: companyData.email.trim().toLowerCase(),
        password: companyData.password,
        data: {
          user_type: 'company'
        }
      };
      
      
      const authResponse = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify(authPayload)
      });

      const authData = await authResponse.json();

      if (!authResponse.ok) {
        const errorMessage = this.handleAuthError(authData, authResponse.status);
        throw { message: errorMessage };
      }

      if (!authData.access_token || !authData.user || !authData.user.id) {
        throw { message: 'No se recibió token de acceso o datos de usuario del servidor' };
      }

      
      const companyPayload = {
        nombre_empresa: companyData.nombre_empresa.trim(),
        nit: companyData.nit.trim(),
        telefono: companyData.telefono.trim(),
        email: companyData.email.trim().toLowerCase(),
        pais_operacion: companyData.pais_operacion.trim(),
        ciudad_operacion: companyData.ciudad_operacion.trim(),
        tamaño_empresa: companyData.tamaño_empresa.trim(),
        sector_empresa: companyData.sector_empresa.trim(),
        estado_empresa: 'activo',
        auth_user_id: authData.user.id
      };


      const companyResponse = await fetch(`${SUPABASE_URL}/rest/v1/company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${authData.access_token}`,
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(companyPayload)
      });

      const insertedCompany = await companyResponse.json();

      if (!companyResponse.ok) {
        
        let errorMessage;
        switch (companyResponse.status) {
          case 422:
            errorMessage = 'Error de validación en datos de empresa: Verifica que todos los campos sean correctos';
            break;
          case 404:
            errorMessage = 'Error: No se encontró la tabla company en Supabase';
            break;
          case 403:
            errorMessage = 'Error de permisos: No tienes autorización para insertar en la tabla company';
            break;
          case 409:
            errorMessage = 'Conflicto: Ya existe una empresa con estos datos (NIT o email duplicado)';
            break;
          default:
            errorMessage = 'Usuario creado pero hubo un error al registrar los datos de la empresa';
        }
        throw { message: errorMessage };
      }

      if (!insertedCompany || insertedCompany.length === 0) {
        throw { message: 'No se pudieron insertar los datos de la empresa' };
      }

      localStorage.setItem('access_token', authData.access_token);
      localStorage.setItem('refresh_token', authData.refresh_token);
      
      const userProfile = {
        id: authData.user.id,
        email: authData.user.email,
        role: 'company',
        companyData: insertedCompany[0]
      };

      localStorage.setItem('user', JSON.stringify(userProfile));

      return {
        token: authData.access_token,
        user: authData.user,
        company: insertedCompany[0],
        session: authData
      };

    } catch (error) {
      if (error.message) {
        throw error;
      } else {
        throw { message: 'Error interno del servidor. Intenta nuevamente.' };
      }
    }
  }
};

// Exportar también los enums para uso en formularios
export { COMPANY_ENUMS };
export default registerCompanyService;